import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BeatLoader } from 'react-spinners';
import {
  ModalOverlay,
  ModalContainer,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalMessage,
  ModalFooter,
  ModalButton,
  ModalIcon,
  CloseButton
} from './styles';

const isPublicAuthPath = () => {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname || '';
  return (
    path === '/' ||
    path.startsWith('/recover') ||
    path.startsWith('/register') ||
    path.startsWith('/invite') ||
    path.startsWith('/reset')
  );
};

const CustomModal = ({
  isOpen,
  onClose,
  type = 'info', // 'success', 'error', 'warning', 'info', 'loading'
  title,
  message,
  confirmText = 'OK',
  onConfirm,
  showCancel = false,
  cancelText = 'Cancelar',
  onCancel,
  inputType = null,         // 👈 novo
  inputValue = '',          // 👈 novo
  onInputChange = () => {},  // 👈 novo
  forceLight = false,
}) => {
  const useLight = forceLight || isPublicAuthPath();

  const isLoading = type === 'loading';

  // Fechar modal com ESC (exceto loading — não deve ser dispensado no meio da requisição)
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isLoading) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, isLoading]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      case 'warning':
        return '⚠️';
      case 'loading':
        return (
          <BeatLoader size={10} color="var(--accent)" loading={true} />
        );
      default:
        return 'ℹ️';
    }
  };

  const handleConfirm = () => {
    if (onConfirm) onConfirm();
    onClose();
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    onClose();
  };

  const handleDismiss = () => {
    // X / overlay: se houver onCancel (Promise dos helpers), resolve; senão só fecha
    if (onCancel) {
      onCancel();
    }
    onClose();
  };

  const handleOverlayClick = (e) => {
    if (isLoading) return;
    if (e.target === e.currentTarget) {
      handleDismiss();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay $forceLight={useLight} onClick={handleOverlayClick}>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
          >
            <ModalContainer type={type} $forceLight={useLight}>
              <ModalContent>
                <ModalHeader>
                  <ModalIcon>{getIcon()}</ModalIcon>
                  <ModalTitle>{title}</ModalTitle>
                  {!isLoading && (
                    <CloseButton type="button" onClick={handleDismiss}>&times;</CloseButton>
                  )}
                </ModalHeader>
                
                <ModalBody>
                  <ModalMessage>{message}</ModalMessage>

                  {/* 📝 Campo de texto opcional */}
                  {inputType === 'text' && (
                    <textarea
                      value={inputValue}
                      onChange={(e) => onInputChange(e.target.value)}
                      placeholder="Descreva o motivo da edição..."
                      rows={4}
                      style={{
                        width: '100%',
                        marginTop: '10px',
                        borderRadius: '12px',
                        border: '2px solid var(--border-default)',
                        background: 'var(--input-bg)',
                        color: 'var(--text-primary)',
                        padding: '0.75rem 1rem',
                        fontSize: '14px',
                        resize: 'vertical',
                        fontFamily: 'inherit',
                        boxSizing: 'border-box',
                      }}
                    />
                  )}
                </ModalBody>
                
                {(type !== 'loading') && (
                <ModalFooter>
                  {showCancel && (
                    <ModalButton 
                      type="cancel" 
                      onClick={handleCancel}
                    >
                      {cancelText}
                    </ModalButton>
                  )}
                  <ModalButton 
                    type={type} 
                    onClick={handleConfirm}
                  >
                    {confirmText}
                  </ModalButton>
                </ModalFooter>
                )}
              </ModalContent>
            </ModalContainer>
          </motion.div>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
};

export default CustomModal;
